import React, { useState } from 'react';

const GrammarUnit8Content = ({ unitData }) => {
  const [currentSection, setCurrentSection] = useState('overview');
  const [showPractice, setShowPractice] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Safety check for unitData and content
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-100 to-orange-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Unit 8 - Data Loading</h1>
            <p className="text-lg text-gray-600">Unit data is being loaded...</p>
          </div>
        </div>
      </div>
    );
  }

  const content = unitData.content;

  const handleAnswerSelect = (questionIndex, selectedOption) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOption
    }));
  };

  const calculateScore = () => {
    if (!content.practiceQuestions) return { correct: 0, total: 0, percentage: 0 };
    
    let correct = 0;
    content.practiceQuestions.forEach((question, index) => {
      if (userAnswers[index] === question.correct) {
        correct++;
      }
    });
    return {
      correct,
      total: content.practiceQuestions.length,
      percentage: Math.round((correct / content.practiceQuestions.length) * 100)
    };
  };

  const renderOverview = () => {
    const section = content.overview;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="bg-yellow-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">核心結構</h3>
          <p className="text-yellow-700 font-mono text-lg">{section.core}</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {section.functions.map((func, index) => (
            <div key={index} className={`p-4 rounded-lg border-2 ${func.color}`}>
              <div className="text-2xl mb-2">{func.icon}</div>
              <h4 className="font-semibold text-gray-800 mb-1">{func.name}</h4>
              <p className="text-sm text-gray-600">{func.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderNounFunction = () => {
    const section = content.nounFunction;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">記憶法</h3>
          <p className="text-blue-700 font-bold">{section.memory.acronym}</p>
          <p className="text-blue-600 text-sm">{section.memory.fullForm}</p>
          <p className="text-blue-700 mt-2">{section.memory.story}</p>
        </div>
        
        {section.patterns.map((pattern, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{pattern.way}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <strong className="text-gray-700">結構：</strong>
                <code className="ml-2 bg-white px-2 py-1 rounded text-sm font-mono">
                  {pattern.structure}
                </code>
              </div>
              <div>
                <strong className="text-gray-700">何時使用：</strong>
                <span className="ml-2">{pattern.when}</span>
              </div>
            </div>
            
            <div className="mb-3">
              <strong className="text-gray-700">如何使用：</strong>
              <span className="ml-2">{pattern.how}</span>
            </div>
            
            <div className="bg-white p-3 rounded border-l-4 border-blue-400 mb-3">
              <div className="font-medium text-gray-800">例句：{pattern.example}</div>
              <div className="text-gray-600 text-sm mt-1">{pattern.translation}</div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
              <p className="text-yellow-700 text-sm"><strong>Tip:</strong> {pattern.tip}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAdjectiveFunction = () => {
    const section = content.adjectiveFunction;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="bg-green-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">記憶法</h3>
          <p className="text-green-700 font-bold">{section.memory.acronym}</p>
          <p className="text-green-600 text-sm">{section.memory.fullForm}</p>
          <p className="text-green-700 mt-2">{section.memory.story}</p>
        </div>
        
        {section.patterns.map((pattern, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{pattern.way}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <strong className="text-gray-700">結構：</strong>
                <code className="ml-2 bg-white px-2 py-1 rounded text-sm font-mono">
                  {pattern.structure}
                </code>
              </div>
              <div>
                <strong className="text-gray-700">何時使用：</strong>
                <span className="ml-2">{pattern.when}</span>
              </div>
            </div>
            
            <div className="mb-3">
              <strong className="text-gray-700">如何使用：</strong>
              <span className="ml-2">{pattern.how}</span>
            </div>
            
            <div className="bg-white p-3 rounded border-l-4 border-green-400 mb-3">
              <div className="font-medium text-gray-800">例句：{pattern.example}</div>
              <div className="text-gray-600 text-sm mt-1">{pattern.translation}</div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
              <p className="text-yellow-700 text-sm"><strong>Tip:</strong> {pattern.tip}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAdverbFunction = () => {
    const section = content.adverbFunction;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-2">記憶法</h3>
          <p className="text-purple-700 font-bold">{section.memory.acronym}</p>
          <p className="text-purple-600 text-sm">{section.memory.fullForm}</p>
          <p className="text-purple-700 mt-2">{section.memory.story}</p>
        </div>
        
        {section.patterns.map((pattern, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{pattern.way}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <strong className="text-gray-700">結構：</strong>
                <code className="ml-2 bg-white px-2 py-1 rounded text-sm font-mono">
                  {pattern.structure}
                </code>
              </div>
              <div>
                <strong className="text-gray-700">何時使用：</strong>
                <span className="ml-2">{pattern.when}</span>
              </div>
            </div>
            
            <div className="mb-3">
              <strong className="text-gray-700">如何使用：</strong>
              <span className="ml-2">{pattern.how}</span>
            </div>
            
            <div className="bg-white p-3 rounded border-l-4 border-purple-400 mb-3">
              <div className="font-medium text-gray-800">例句：{pattern.example}</div>
              <div className="text-gray-600 text-sm mt-1">{pattern.translation}</div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
              <p className="text-yellow-700 text-sm"><strong>Tip:</strong> {pattern.tip}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSpecialUses = () => {
    const section = content.specialUses;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{section.title}</h2>
        
        <div className="bg-orange-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-orange-800 mb-2">記憶法</h3>
          <p className="text-orange-700 font-bold">{section.memory.acronym}</p>
          <p className="text-orange-600 text-sm">{section.memory.fullForm}</p>
          <p className="text-orange-700 mt-2">{section.memory.story}</p>
        </div>
        
        {section.patterns.map((pattern, index) => (
          <div key={index} className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">{pattern.way}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <strong className="text-gray-700">結構：</strong>
                <code className="ml-2 bg-white px-2 py-1 rounded text-sm font-mono">
                  {pattern.structure}
                </code>
              </div>
              <div>
                <strong className="text-gray-700">何時使用：</strong>
                <span className="ml-2">{pattern.when}</span>
              </div>
            </div>
            
            <div className="mb-3">
              <strong className="text-gray-700">如何使用：</strong>
              <span className="ml-2">{pattern.how}</span>
            </div>
            
            <div className="bg-white p-3 rounded border-l-4 border-orange-400 mb-3">
              <div className="font-medium text-gray-800">例句：{pattern.example}</div>
              <div className="text-gray-600 text-sm mt-1">{pattern.translation}</div>
            </div>
            
            <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
              <p className="text-yellow-700 text-sm"><strong>Tip:</strong> {pattern.tip}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMemoryCards = () => {
    const section = content.memoryCards;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">記憶卡片 Memory Cards</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.map((card, index) => (
            <div key={index} className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">{card.front}</h3>
              <p className="text-yellow-700 mb-2">{card.back}</p>
              <p className="text-sm text-yellow-600 mb-2">例：{card.example}</p>
              <p className="text-xs text-yellow-600 italic">{card.tip}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPracticeSection = () => {
    if (!content.practiceQuestions) return null;

    const score = showResults ? calculateScore() : null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b-3 border-blue-500 pb-3">
          練習題 - Practice Questions
        </h2>

        {!showResults ? (
          <>
            {content.practiceQuestions.map((question, qIndex) => (
              <div key={qIndex} className="bg-gray-50 p-5 rounded-lg mb-4 border border-gray-200">
                <h4 className="text-gray-800 mb-4 font-medium">
                  {qIndex + 1}. {question.question}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        userAnswers[qIndex] === oIndex 
                          ? 'border-yellow-500 bg-yellow-500 text-white' 
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              onClick={() => setShowResults(true)}
              disabled={Object.keys(userAnswers).length < content.practiceQuestions.length}
              className={`px-6 py-3 rounded-lg text-white font-medium transition-all ${
                Object.keys(userAnswers).length < content.practiceQuestions.length
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              提交答案 Submit Answers
            </button>
          </>
        ) : (
          <div>
            <div className={`p-5 rounded-lg mb-5 text-center ${
              score.percentage >= 70 ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <h3 className={`text-xl font-bold mb-2 ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                測驗結果 - Test Results
              </h3>
              <p className={`text-2xl font-bold ${
                score.percentage >= 70 ? 'text-green-800' : 'text-red-800'
              }`}>
                {score.correct} / {score.total} ({score.percentage}%)
              </p>
            </div>

            {content.practiceQuestions.map((question, qIndex) => {
              const userAnswer = userAnswers[qIndex];
              const isCorrect = userAnswer === question.correct;
              
              return (
                <div key={qIndex} className={`p-4 rounded-lg mb-3 border-2 ${
                  isCorrect ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {qIndex + 1}. {question.question}
                  </h4>
                  <p className={`mb-2 ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    Your answer: {question.options[userAnswer]} {isCorrect ? '✓' : '✗'}
                  </p>
                  {!isCorrect && (
                    <p className="text-red-800 mb-2">
                      Correct answer: {question.options[question.correct]}
                    </p>
                  )}
                  <p className={`text-sm italic ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {question.explanation}
                  </p>
                </div>
              );
            })}

            <button
              onClick={() => {
                setShowResults(false);
                setUserAnswers({});
              }}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
            >
              重新測驗 Retake Test
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderMemoryTips = () => {
    const section = content.memoryTips;
    if (!section) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 border-b-3 border-yellow-500 pb-3">
          💡 {section.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.tips.map((tip, index) => (
            <div key={index} className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">{tip.category}</h4>
              <p className="text-yellow-700 text-sm">{tip.content}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentSection) {
      case 'overview':
        return renderOverview();
      case 'nounFunction':
        return renderNounFunction();
      case 'adjectiveFunction':
        return renderAdjectiveFunction();
      case 'adverbFunction':
        return renderAdverbFunction();
      case 'specialUses':
        return renderSpecialUses();
      case 'memoryCards':
        return renderMemoryCards();
      case 'practice':
        return renderPracticeSection();
      case 'memoryTips':
        return renderMemoryTips();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 to-orange-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {unitData.title.english}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {unitData.subtitle.english}
          </p>
          
          {/* Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <button
              onClick={() => setCurrentSection('overview')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'overview' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setCurrentSection('nounFunction')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'nounFunction' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Noun Function
            </button>
            <button
              onClick={() => setCurrentSection('adjectiveFunction')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'adjectiveFunction' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Adjective Function
            </button>
            <button
              onClick={() => setCurrentSection('adverbFunction')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'adverbFunction' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Adverb Function
            </button>
            <button
              onClick={() => setCurrentSection('specialUses')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'specialUses' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Special Uses
            </button>
            <button
              onClick={() => setCurrentSection('memoryCards')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'memoryCards' && !showPractice
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Memory Cards
            </button>
            <button
              onClick={() => setCurrentSection('practice')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'practice'
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-blue-500 text-blue-500 hover:bg-blue-50'
              }`}
            >
              Practice
            </button>
            <button
              onClick={() => setCurrentSection('memoryTips')}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                currentSection === 'memoryTips'
                  ? 'border-yellow-500 bg-yellow-500 text-white'
                  : 'border-yellow-500 text-yellow-500 hover:bg-yellow-50'
              }`}
            >
              Memory Tips
            </button>
          </div>
        </div>

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default GrammarUnit8Content;
